import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function RoadmapSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const milestones = [
    {
      quarterKey: "roadmap.milestone1.quarter",
      titleKey: "roadmap.milestone1.title",
      descKey: "roadmap.milestone1.desc",
      status: "current" as const,
    },
    {
      quarterKey: "roadmap.milestone2.quarter",
      titleKey: "roadmap.milestone2.title",
      descKey: "roadmap.milestone2.desc",
      status: "upcoming" as const,
    },
    {
      quarterKey: "roadmap.milestone3.quarter",
      titleKey: "roadmap.milestone3.title",
      descKey: "roadmap.milestone3.desc",
      status: "upcoming" as const,
    },
    {
      quarterKey: "roadmap.milestone4.quarter",
      titleKey: "roadmap.milestone4.title",
      descKey: "roadmap.milestone4.desc",
      status: "future" as const,
    },
  ];

  return (
    <section
      id="roadmap"
      ref={sectionRef}
      className="bg-ax-dark py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-end gap-6 md:gap-12 mb-16">
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
            <span className="text-xs text-ax-silver-dark tracking-[0.3em] uppercase block mb-4">
              {t("roadmap.label")}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-bold">
              {t("roadmap.title")}
            </h2>
          </div>
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-200">
            <p className="text-ax-silver-dark text-sm md:text-base max-w-md">
              {t("roadmap.description")}
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute top-6 left-0 right-0 h-[2px] bg-white/10 hidden md:block" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
            {milestones.map((m, i) => (
              <div
                key={t(m.quarterKey)}
                className="animate-in opacity-0 translate-y-6 transition-all duration-700 relative"
                style={{ transitionDelay: `${200 + i * 200}ms` }}
              >
                {/* Dot on timeline */}
                <div className="hidden md:flex items-center mb-6">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 z-10 ${
                      m.status === "current"
                        ? "bg-ax-red border-ax-red"
                        : "bg-ax-dark border-ax-silver-dark"
                    }`}
                  />
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-ax-red/30 transition-colors">
                  <span
                    className={`inline-block text-xs font-medium px-3 py-1 rounded-full mb-4 ${
                      m.status === "current"
                        ? "bg-ax-red/20 text-ax-red"
                        : "bg-white/10 text-ax-silver-dark"
                    }`}
                  >
                    {t(m.quarterKey)}
                  </span>
                  <h3 className="font-serif text-lg font-bold text-white mb-3">
                    {t(m.titleKey)}
                  </h3>
                  <p className="text-white/40 text-sm leading-relaxed">
                    {t(m.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom brand line */}
        <div className="mt-20 text-center animate-in opacity-0 translate-y-6 transition-all duration-700 delay-1000">
          <p className="font-serif text-2xl md:text-3xl text-white/80 font-light italic">
            {t("roadmap.bottomLine1")}
          </p>
          <p className="text-ax-silver-dark text-sm mt-2 tracking-widest">
            {t("roadmap.bottomLine2")}
          </p>
        </div>
      </div>
    </section>
  );
}